import React, { useContext, useState } from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { IconButton, type IconButtonProps } from '@fiverr-private/button';
import { LinkedinIcon, TwitterIcon, EnvelopeIcon, LinkIcon, FacebookIcon, WhatsappIcon } from '@fiverr-private/visuals';
import { translate } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import { FROM_APP_QUERY_PARAM } from '../../../../constants';
import { sendEvent } from '../../../../components/EventReportingManager/utils';
import { EventReportingManagerContext } from '../../../../components/EventReportingManager';
import { getEventContext } from '../../auto_registration_components/helpers/getEventContext';
import { copiedClass } from './styles.ve.css';

const COMPONENT_NAME = 'SocialSharing';
const EVENT_NAME_PREFIX = 'Share on';

interface SocialSharingType extends Pick<IconButtonProps, 'size' | 'colorScheme' | 'variant' | 'intent'> {
    facebook: boolean;
    whatsapp: boolean;
    twitter: boolean;
    linkedin: boolean;
    email: boolean;
    copyLink: boolean;
    emailSubject: string;
    whatsappText: string;
}

const SocialSharing = ({
    facebook,
    whatsapp,
    twitter,
    linkedin,
    email,
    copyLink,
    emailSubject,
    whatsappText,
    size,
    colorScheme,
    variant,
    intent,
}: SocialSharingType) => {
    const { url, queryParameters } = getContext();
    const [copied, setCopied] = useState(false);
    const { context: managerContext, enabled } = useContext(EventReportingManagerContext);

    const onSocialSharingClick = (platform: string) => {
        if (enabled) {
            const eventName = `${EVENT_NAME_PREFIX} ${platform}`;
            const context = getEventContext({
                componentName: COMPONENT_NAME,
                eventEnrichment: {},
                eventName,
            });

            sendEvent(eventName, 'click', context, managerContext);
        }
    };

    const copyUrl = () => {
        onSocialSharingClick('Copy Link');
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 1000);
    };

    if (queryParameters[FROM_APP_QUERY_PARAM] === 'true') {
        return null;
    }

    return (
        <Stack withDivider gap="1">
            {facebook && (
                <IconButton
                    variant={variant}
                    size={size}
                    intent={intent}
                    colorScheme={colorScheme}
                    aria-label={translate<string>('builder_components.social_sharing.facebook')}
                    href={`https://twitter.com/share?url=${url}`}
                    target="_blank"
                    onClick={() => onSocialSharingClick('Facebook')}
                >
                    <FacebookIcon />
                </IconButton>
            )}
            {whatsapp && (
                <IconButton
                    variant={variant}
                    size={size}
                    intent={intent}
                    colorScheme={colorScheme}
                    aria-label={translate<string>('builder_components.social_sharing.whatsapp')}
                    href={`whatsapp://send?text=${whatsappText}=${url}`}
                    target="_blank"
                    onClick={() => onSocialSharingClick('Whatsapp')}
                >
                    <WhatsappIcon />
                </IconButton>
            )}
            {twitter && (
                <IconButton
                    variant={variant}
                    size={size}
                    intent={intent}
                    colorScheme={colorScheme}
                    aria-label={translate<string>('builder_components.social_sharing.twitter')}
                    href={`https://twitter.com/share?url=${url}`}
                    target="_blank"
                    onClick={() => onSocialSharingClick('Twitter')}
                >
                    <TwitterIcon />
                </IconButton>
            )}
            {linkedin && (
                <IconButton
                    variant={variant}
                    size={size}
                    intent={intent}
                    colorScheme={colorScheme}
                    aria-label={translate<string>('builder_components.social_sharing.linkedin')}
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${url}`}
                    target="_blank"
                    onClick={() => onSocialSharingClick('Linkedin')}
                >
                    <LinkedinIcon />
                </IconButton>
            )}
            {email && (
                <IconButton
                    variant={variant}
                    size={size}
                    intent={intent}
                    colorScheme={colorScheme}
                    aria-label={translate<string>('builder_components.social_sharing.email')}
                    href={`mailto:?body=${url}&subject=${emailSubject}`}
                    onClick={() => onSocialSharingClick('Email')}
                >
                    <EnvelopeIcon />
                </IconButton>
            )}
            {copyLink && (
                <IconButton
                    variant={variant}
                    size={size}
                    intent={intent}
                    colorScheme={colorScheme}
                    aria-label={translate<string>('builder_components.social_sharing.copy_link')}
                    onClick={copyUrl}
                    className={copied ? copiedClass : undefined}
                >
                    <LinkIcon />
                </IconButton>
            )}
        </Stack>
    );
};

export default SocialSharing;
